import { Constants } from "../Constants";
import BaseApi from "./BaseApi";
import dateTimeHelper from "../util/dateTimeHelper";
import stringHelper from "../util/stringHelper";

class SafeFunApi extends BaseApi {
  constructor() {
    super(
      process.env.REACT_APP_baseApiUrl,
      Constants.USER_TOKEN_KEY,
      Constants.USER_TOKEN_EXPIRATION
    );
    this.getCurrentUser = this.getCurrentUser.bind(this);
    this.createProfile = this.createProfile.bind(this);
    this.getTestResults = this.getTestResults.bind(this);
    this.getVisits = this.getVisits.bind(this);
    this.addTestResultURL = this.addTestResultURL.bind(this);
    this.generateTestResultTextObjectsUrl = this.generateTestResultTextObjectsUrl.bind(
      this
    );
  }

  async getCurrentUser() {
    const response = await this.getAxios().get("/consumer");
    return response;
  }

  async getSafeWindow() {
    const response = await this.getAxios().get("/consumer/getsafewindow");
    return response;
  }

  async getSubscribers() {
    const response = await this.getAxios().get(
      "/consumersubscription/subscribers/"
    );
    return response;
  }

  async getPublishers() {
    const response = await this.getAxios().get(
      "/consumersubscription/publishers/"
    );
    return response;
  }

  async searchPublishers(searchString) {
    const params = {
      params: { searchString },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/searchpublishers/",
      params
    );
    return response;
  }

  async requestSubscription(sfConsumerId) {
    const params = {
      params: { sfConsumerId },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/requestsubscription/",
      params
    );
    return response;
  }

  async cancelSubscriptionRequest(sfConsumerSubscriptionId) {
    const params = {
      params: { sfConsumerSubscriptionId },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/cancelsubscriptionrequest/",
      params
    );
    return response;
  }

  async confirmSubscriptionRequest(sfConsumerSubscriptionId) {
    const params = {
      params: { sfConsumerSubscriptionId },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/confirmsubscriptionrequest/",
      params
    );
    return response;
  }

  async declineSubscriptionRequest(sfConsumerSubscriptionId) {
    const params = {
      params: { sfConsumerSubscriptionId },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/declinesubscriptionrequest/",
      params
    );
    return response;
  }

  async deleteSubscription(sfConsumerSubscriptionId) {
    const params = {
      params: { sfConsumerSubscriptionId },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/deletesubscription/",
      params
    );
    return response;
  }

  async getSubscriptionStatus(sfConsumerId) {
    const params = {
      params: { sfConsumerId },
    };
    const response = await this.getAxios().get(
      "/consumersubscription/subscriptionstatus/",
      params
    );
    return response;
  }

  async getPermissions() {
    const response = await this.getAxios().get("/consumer/getpermissions");
    //remove all sceen keys to get a clean slate
    window.localStorage.removeItem(Constants.MYWINDOW_ENABLED_KEY);
    window.localStorage.removeItem(Constants.MYTESTS_ENABLED_KEY);
    window.localStorage.removeItem(Constants.MYFUN_ENABLED_KEY);
    window.localStorage.removeItem(Constants.LOCATIONS_ENABLED_KEY);
    window.localStorage.removeItem(Constants.SETTINGS_ENABLED_KEY);
    window.localStorage.removeItem(Constants.FRIENDS_ENABLED_KEY);

    if (response.data) {
      const screens = response.data.enabledScreens;
      var i;
      for (i = 0; i < screens.length; i++) {
        window.localStorage.setItem(screens[i] + "Enabled", Constants.ENABLED);
      }
    }
    return response;
  }

  async searchLocations(radius, latitude, longitude, postalCode) {
    const params = {
      params: { latitude, longitude, miles: radius, zip: postalCode },
    };
    const response = await this.getAxios().get("/search/", params);
    return response;
  }

  async createProfile(firstName, middleName, lastName, dob, zip) {
    const sFConsumer = {
      SF_ConsumerId: 0,
      identityUserId: "",
      firstName: encodeURIComponent(firstName),
      middleName: stringHelper.isNullOrEmpty(middleName)
        ? null
        : encodeURIComponent(middleName),
      lastName: encodeURIComponent(lastName),
      dob: encodeURIComponent(dateTimeHelper.formattedDateToDbDate(dob)),
      postalCode: encodeURIComponent(zip),
      marketingOptOut: 0,
      healthUpdatesOptOut: 0,
    };
    let latitude = 0;
    let longitude = 0;

    /*
    let { status } = await Location.requestPermissionsAsync();
    if (status === "granted") {
      //Get the lat/long
      let location = await Location.getCurrentPositionAsync({});
      latitude = location.coords.latitude;
      longitude = location.coords.longitude;
    }
    */

    const obj = { consumer: sFConsumer, latitude, longitude };
    const response = await this.getAxios().post("/consumer/createprofile", obj);
    return response;
  }

  async saveUser(consumer) {
    const response = await this.getAxios().post(
      "/consumer/modifycore/",
      consumer
    );
    return response;
  }

  async getTestResults() {
    const response = await this.getAxios().get(
      "/testresult/getcurrentusertestresults/"
    );
    return response;
  }

  async getTestResultPropertyValues(sfTestResultId) {
    const params = {
      params: { sfTestResultId },
    };

    const response = await this.getAxios().get(
      "/testresult/testresultpropertyvalues/",
      params
    );
    return response;
  }

  async getVisits() {
    const response = await this.getAxios().get(
      "/locationvisit/getcurrentuservisits/"
    );
    return response;
  }

  async setRating(locationVisit) {
    const response = await this.getAxios().post(
      "/locationvisit/setrating/",
      locationVisit
    );
    return response;
  }

  async addTestResultURL(url) {
    const params = { params: { url: url } };
    const response = await this.getAxios().get(
      "/testresult/addtestresulturl/",
      params,
      this.formEncoded
    );
    return response;
  }

  async addTestResultFile(fileUri) {
    const data = new FormData();
    data.append("multipartData", fileUri);
    const response = await this.getAxios().post(
      "/testresult/addtestresultmultipart/",
      data,
      this.multiPartEncoded
    );
    return response;
  }

  async addTestResultLink(url) {
    const obj = { url, criteria: [] };
    const response = await this.getAxios().post(
      "/testresult/addtestresultlink/",
      obj
    );
    return response;
  }

  async generateTestResultTextObjectsUrl(url) {
    const params = { params: { url: url } };
    const response = await this.getAxios().get(
      "/testresult/generatetestresulttextobjectsurl/",
      params,
      this.formEncoded
    );
    return response;
  }

  async getTestResultImage(testResultId) {
    const response = await this.getAxios().get("/testresult/getimage/", {
      params: { testResultId },
    });
    return response;
  }

  async disputeTestResult(disputeData) {
    const response = await this.getAxios().post(
      "/testresult/dispute/",
      disputeData
    );
    return response;
  }
}

export default SafeFunApi;
